import { compareOrder } from "../../api/src/shared/ordering.js";
import type { Item, List } from "./api.js";

/**
 * Today and Upcoming, as groupings over `due_date`.
 *
 * Pure on purpose: `(lists, items, today)` in, groups out, no IndexedDB and no
 * clock. The calendar stays out of the store — the store knows rows, and what
 * "today" means is a question only the device can answer — so the boundary
 * arrives as a `YYYY-MM-DD` string from `format.todayIso()` and every
 * comparison here is a string comparison, which sorts correctly because the
 * format is fixed-width.
 *
 * Each list owns its order, and nothing here changes one. The groups run in
 * the lists' own order, and the order inside a group is the caller's choice
 * of reading (g91): Today reads each list as the list has it, and Upcoming
 * reads it by due date, nearest first. Neither is an order a person can edit
 * from these screens — `docs/product-design-system.md` has the rule.
 * Membership is read off the data rather than off a list's kind:
 * a shopping list hides the due field, but an item that carries one — set
 * before the kind changed, or by an agent — is due like any other.
 */

/**
 * Unchecked and due on or before today, **plus** checked and due exactly today.
 *
 * The second clause is what lets something you ticked off stay visibly done for
 * the rest of the day, in place, and be simply gone tomorrow. It is deliberately
 * not "checked and due on or before today": that never lets go, so every
 * completed overdue item would accumulate in Today for the life of the account.
 */
export function inToday(item: Item, today: string): boolean {
  if (item.repeat && item.lastDoneOn === today) return true;
  if (!item.due) return false;
  return item.done ? item.due.date === today : item.due.date <= today;
}

/** The same shape for the other side of the boundary, and unbounded. */
export function inUpcoming(item: Item, today: string): boolean {
  if (!item.due) return false;
  return item.due.date > today;
}

/** How the rows inside one group are read. Total: only a row and itself tie. */
export type ItemOrder = (a: Item, b: Item) => number;

/** The list's own order: the key, then the id. What `inDisplayOrder` sorts by. */
export const listOrder: ItemOrder = compareOrder;

/**
 * Nearest due date first (g91). On one date, an item with no time leads, then
 * the times as written, then the list's own order.
 *
 * Times compare as their `HH:MM` strings and the zone is ignored: a due date
 * is a calendar fact, not an instant, and an order worked out from instants
 * would disagree with the time each row displays. An item with no due date is
 * not a member of anything this orders; it sorts last so the order is total.
 */
export const dueOrder: ItemOrder = (a, b) => {
  if (a.due && b.due) {
    if (a.due.date !== b.due.date) return a.due.date < b.due.date ? -1 : 1;
    const [at, bt] = [a.due.time, b.due.time];
    if (at !== bt) {
      if (at === null) return -1;
      if (bt === null) return 1;
      return at < bt ? -1 : 1;
    }
  } else if (a.due || b.due) {
    return a.due ? -1 : 1;
  }
  return compareOrder(a, b);
};

export interface Group {
  list: List;
  items: Item[];
}

export interface Projection {
  groups: Group[];
  /**
   * Members the budget left out, by id.
   *
   * Ids rather than a count because the screen has to tell a row it decided
   * not to draw from one it has never heard of: an arriving change for the
   * first must stay hidden, and for the second must appear.
   */
  hidden: string[];
}

/**
 * The members, grouped by list, with at most `budget` rows across the whole
 * projection.
 *
 * The budget is rows rather than groups because the case it exists for is one
 * list holding thousands of dated items, which a group count does nothing
 * about. Groups are walked in order and the one that crosses the boundary is
 * rendered partially, so the cut never depends on how the members happen to be
 * distributed. The cut is made in `order`, so under `dueOrder` it is the
 * furthest dates of a group that go behind the control.
 */
export function project(
  lists: List[],
  items: Item[],
  member: (item: Item) => boolean,
  budget = Number.POSITIVE_INFINITY,
  order: ItemOrder = listOrder,
): Projection {
  const byList = new Map<string, Item[]>();
  for (const item of items) {
    if (!member(item)) continue;
    const bucket = byList.get(item.listId);
    if (bucket) bucket.push(item);
    else byList.set(item.listId, [item]);
  }

  const groups: Group[] = [];
  const hidden: string[] = [];
  let room = budget;

  // `lists` is already in order — `store.lists()` sorts by `orderKey` — and a
  // member whose list is missing from the mirror is skipped rather than given a
  // group of its own: a heading needs a name, and the delta that carries the
  // list always lands before the one that carries its items.
  for (const list of lists) {
    const members = byList.get(list.id);
    if (!members) continue;

    const ordered = [...members].sort(order);
    if (room <= 0) {
      for (const item of ordered) hidden.push(item.id);
      continue;
    }

    const shown = ordered.slice(0, room);
    for (const item of ordered.slice(shown.length)) hidden.push(item.id);
    room -= shown.length;
    groups.push({ list, items: shown });
  }

  return { groups, hidden };
}
